/**
 * PaymentsPage — o posto de trabalho do financeiro no ato de pagar.
 *
 * O ato foi desmembrado em três passos, entre dois times:
 *
 *   1. entrada do dinheiro   (financeiro) → o botão "Registrar Pagamento" desta tela
 *   2. marcação de títulos   (cobrança)   → outras telas, ainda por construir
 *   3. efetivação            (financeiro) → a ação "Aprovar Identificação" desta tela
 *
 * O recorte é UM DIA, e não uma paginação livre: o financeiro fecha o dia, não navega histórico.
 * Ainda assim a tabela é o `PageList` das demais telas — o componente traz cabeçalho, ordenação,
 * rodapé e estados de carga/erro já resolvidos, e uma tabela artesanal aqui só criaria uma
 * segunda maneira de ler a mesma coisa.
 *
 * O fuso vai na URL porque `payment_date` é gravado em UTC: sem ele, um PIX das 22h em São Paulo
 * cairia no fechamento do dia seguinte.
 *
 * ⚠️ Enquanto o passo 2 não existir, nenhum pagamento chega a SINALIZADO por meio da aplicação —
 * é um gap conhecido desta entrega, não um defeito da tela.
 */
import { useCallback, useMemo, useState } from 'react';
import {
    FaClipboard, FaClipboardCheck, FaClipboardList, FaEllipsisV, FaFileInvoiceDollar,
    FaDownload, FaInfoCircle, FaChevronDown, FaChevronRight, FaPlus,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

import PageList from '../../templates/PageList';
import DateFilter from '../../molecules/DateFilter';
import PaymentsPanel, { STATUS_META } from './PaymentsPanel';
import RegisterPaymentModal from './modals/RegisterPaymentModal';
import PaymentInvoicesModal from './modals/PaymentInvoicesModal';
import PaymentInfoModal from './modals/PaymentInfoModal';
import DeletePaymentDialog from './modals/DeletePaymentDialog';
import { PaymentRow, PaymentStatus, approveIdentification } from './services/billingApi';
import appConfig from '../../../config/app-config';
import { formatBRL } from '../../../utils/currencyUtils';
import { formatTaxId } from '../../../utils/taxIdUtils';

const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

const hoje = () => new Date().toISOString().split('T')[0];

/** Ícone por estado. Vai com `title` e `aria-label`: ícone sozinho não se lê. */
const STATUS_ICON: Record<PaymentStatus, { Icon: typeof FaClipboard; cor: string }> = {
    PENDING: { Icon: FaClipboard, cor: 'text-slate-400' },
    MARKED: { Icon: FaClipboardList, cor: 'text-amber-500' },
    IDENTIFIED: { Icon: FaClipboardCheck, cor: 'text-green-600' },
};

const TIPO_LABEL: Record<string, string> = {
    FINANCIAL: 'Financeiro',
    DEBT_CONFESSION: 'Confissão de Dívida',
    ASSET_DATION: 'Dação de Bens',
};

const DIREITA = 'text-right';

/**
 * Fundo da linha por estado — o que já está resolvido some do caminho, o que espera aprovação
 * salta. Tom SUAVE de propósito: a tabela tem texto em cima, e cor forte aqui competiria com a
 * leitura em vez de guiá-la. Pendente fica sem cor: é o estado normal, e pintar tudo não destaca
 * nada.
 */
const FUNDO_POR_ESTADO: Record<PaymentStatus, string> = {
    PENDING: 'hover:bg-gray-50 dark:hover:bg-slate-700',
    MARKED: 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30',
    IDENTIFIED: 'bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30',
};

/**
 * O botão do menu tem SEMPRE o mesmo texto — "Aprovar Identificação" — e fica inativo fora de
 * SINALIZADO. O porquê de estar inativo vira tooltip: trocar o texto do botão pelo motivo fazia a
 * ação mudar de nome conforme a linha, e o usuário não reconhecia mais o comando que procurava.
 */
const APROVAR_TOOLTIP: Record<PaymentStatus, string> = {
    MARKED: 'Aprovar Identificação de Pagamentos: Efetiva o registro de pagamento dos títulos elencados',
    PENDING: 'Aguardando a cobrança sinalizar as faturas deste pagamento',
    IDENTIFIED: 'Identificação já aprovada',
};

/** Excluir é possível enquanto nada foi efetivado — depois disso o caminho é estorno. */
const EXCLUIR_TOOLTIP: Record<PaymentStatus, string> = {
    MARKED: 'Excluir o pagamento. As faturas sinalizadas perdem a marcação',
    PENDING: 'Excluir o pagamento',
    IDENTIFIED: 'Pagamento já identificado não pode ser excluído — as faturas estão pagas e o settlement já movimentou os títulos',
};

const PaymentsPage: React.FC = () => {
    const [date, setDate] = useState(hoje());
    // as linhas que o PageList buscou — o painel reparte as MESMAS, e buscá-las de novo por
    // fora duplicaria a chamada e abriria espaço para os dois divergirem
    const [rows, setRows] = useState<PaymentRow[]>([]);
    const [painelAberto, setPainelAberto] = useState(true);
    const [registrando, setRegistrando] = useState(false);
    const [verFaturasDe, setVerFaturasDe] = useState<PaymentRow | null>(null);
    const [verInfoDe, setVerInfoDe] = useState<PaymentRow | null>(null);
    const [menuAberto, setMenuAberto] = useState<string | null>(null);
    const [aprovando, setAprovando] = useState<string | null>(null);
    const [excluindo, setExcluindo] = useState<PaymentRow | null>(null);
    // muda a URL para o PageList refazer a busca depois de registrar ou aprovar
    const [refreshKey, setRefreshKey] = useState(0);
    // vazio = ordem do servidor (mais recente primeiro), que é como o financeiro trabalha
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

    const fetchHeaders = useMemo(() => [{ key: 'x-api-key', value: API_KEY || '' }], []);

    const url = useMemo(() => {
        // o fuso é o do NAVEGADOR: é onde o usuário está, e é o dia que ele enxerga
        const params = new URLSearchParams({
            date,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        // o servidor ordena: o dia vem numa página só, mas ordenar no cliente criaria uma segunda
        // semântica de empate e divergiria do `status`, que é derivado lá
        if (sortField) params.set('sort', `${sortOrder === 'DESC' ? '-' : ''}${sortField}`);
        return `${API_URL}/payments?${params}&_r=${refreshKey}`;
    }, [date, refreshKey, sortField, sortOrder]);

    const recarregar = useCallback(() => setRefreshKey(k => k + 1), []);

    const tableHeaders = useMemo(() => [
        // o default do ATable já é text-center; declaramos só quem foge dele.
        // `field` casa com as chaves de _SORT_KEYS no list_payments_query.
        { id: 'status', title: 'Estado', canSort: true, field: 'status' },
        { id: 'amount', title: 'Valor', canSort: true, field: 'amount', cssClass: DIREITA },
        { id: 'payment_method', title: 'Meio', canSort: true, field: 'payment_method' },
        { id: 'payment_type', title: 'Tipo', canSort: true, field: 'payment_type' },
        { id: 'payer', title: 'Pagador', canSort: true, field: 'payer' },
        {
            id: 'receiving_bank_account', title: 'Conta de Recebimento', canSort: true,
            field: 'receiving_bank_account'
        },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const aprovar = async (row: PaymentRow) => {
        setMenuAberto(null);
        setAprovando(row.paymentId);
        try {
            await approveIdentification(row.paymentId);
            toast.success('Identificação aprovada. As faturas foram liquidadas.');
            recarregar();
        } catch (e: any) {
            toast.error(`Não foi possível aprovar: ${e.message}`);
        } finally {
            setAprovando(null);
        }
    };

    const renderRows = (linhas: any[]) => linhas.map((row: PaymentRow) => {
        const { Icon, cor } = STATUS_ICON[row.status];
        const rotulo = STATUS_META[row.status].label;
        return (
            <tr key={row.paymentId} className={FUNDO_POR_ESTADO[row.status]}>
                <td className="px-4 py-2 text-center">
                    <Icon className={`inline ${cor}`} size={18}
                        title={rotulo} aria-label={rotulo} role="img" />
                </td>
                <td className="px-4 py-2 text-sm text-right font-medium">
                    {row.amount != null ? formatBRL(row.amount) : '-'}
                </td>
                {/* meio é NULL fora de FINANCIAL — dado correto, não ausência */}
                <td className="px-4 py-2 text-sm text-center">{row.paymentMethod || '—'}</td>
                <td className="px-4 py-2 text-sm text-center">
                    {TIPO_LABEL[row.paymentType] || row.paymentType}
                </td>
                <td className="px-4 py-2 text-sm text-center max-w-[240px] truncate"
                    title={row.payer?.documentNumber ? formatTaxId(row.payer.documentNumber) : ''}>
                    {row.payer?.name || '—'}
                </td>
                <td className="px-4 py-2 text-sm text-center">
                    {row.receivingBankAccount?.exibitionName || '—'}
                </td>
                <td className="px-4 py-2 text-sm text-center whitespace-nowrap">
                    <button onClick={() => setVerFaturasDe(row)}
                        title={`Faturas deste pagamento (${row.linkedInvoicesCount})`}
                        aria-label="Ver faturas"
                        className="px-2 py-1 text-gray-500 hover:text-blue-600">
                        <FaFileInvoiceDollar />
                    </button>
                    {/* só aparece quando há recibo: botão morto sugere que o arquivo existe e
                        que o download falhou */}
                    {row.fileUrlReceipt && (
                        <a href={row.fileUrlReceipt} target="_blank" rel="noreferrer"
                            title="Baixar recibo" aria-label="Baixar recibo"
                            className="px-2 py-1 text-gray-500 hover:text-blue-600 inline-block">
                            <FaDownload />
                        </a>
                    )}
                    <button onClick={() => setVerInfoDe(row)}
                        title="Mais informações" aria-label="Mais informações"
                        className="px-2 py-1 text-gray-500 hover:text-blue-600">
                        <FaInfoCircle />
                    </button>
                    <span className="relative inline-block">
                        <button
                            onClick={() => setMenuAberto(m => m === row.paymentId ? null : row.paymentId)}
                            title="Ações do pagamento" aria-label="Ações do pagamento"
                            className="px-2 py-1 text-gray-500 hover:text-blue-600">
                            <FaEllipsisV />
                        </button>
                        {menuAberto === row.paymentId && (
                            <div className="absolute right-0 z-10 mt-1 w-56 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded shadow-lg text-left">
                                {/* ⚠️ ativo só em SINALIZADO: em PENDENTE liquidaria fatura com a
                                    conta aberta; em IDENTIFICADO reemitiria INVOICE_PAID de fatura
                                    já paga, e o settlement moveria o mesmo dinheiro de novo */}
                                <button onClick={() => aprovar(row)}
                                    disabled={row.status !== 'MARKED' || aprovando === row.paymentId}
                                    title={APROVAR_TOOLTIP[row.status]}
                                    className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed">
                                    {aprovando === row.paymentId ? 'Aprovando...' : 'Aprovar Identificação'}
                                </button>
                                {/* enquanto nada foi efetivado, o pagamento pode ser desfeito */}
                                <button onClick={() => { setMenuAberto(null); setExcluindo(row); }}
                                    disabled={row.status === 'IDENTIFIED'}
                                    title={EXCLUIR_TOOLTIP[row.status]}
                                    className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed">
                                    Excluir Pagamento
                                </button>
                            </div>
                        )}
                    </span>
                </td>
            </tr>
        );
    });

    return (
        <>
            <PageList
                pageTitle="Pagamentos"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Financeiro', url: '#' },
                    { label: 'Pagamentos', url: '/billing/payments' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={sortField}
                tableOrderSort={sortOrder}
                onTableSort={(field: string, order: 'ASC' | 'DESC') => {
                    setSortField(field);
                    setSortOrder(order);
                }}
                renderRows={renderRows}
                page={0}
                pageSize={200}
                onPageChange={() => { }}
                onItemsPerPageChange={() => { }}
                itemsLabel="pagamentos"
                onGotApiPayload={(data: any) => setRows(data?.list || [])}
            >
                {{
                    filterSlot: (
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mb-1">
                            <button
                                onClick={() => setPainelAberto(a => !a)}
                                aria-expanded={painelAberto}
                                className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200"
                            >
                                {painelAberto ? <FaChevronDown /> : <FaChevronRight />}
                                Situação do dia
                            </button>
                            {painelAberto && (
                                <div className="px-4 pb-4"><PaymentsPanel rows={rows} /></div>
                            )}
                        </div>
                    ),
                    controlSlot: (
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                            <DateFilter label="Data do pagamento" value={date} onChange={setDate} />
                            <button
                                onClick={() => setRegistrando(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2 text-sm"
                            >
                                <FaPlus /> Registrar Pagamento
                            </button>
                        </div>
                    ),
                }}
            </PageList>

            {registrando && (
                <RegisterPaymentModal onClose={() => setRegistrando(false)} onRegistered={recarregar} />
            )}
            {verFaturasDe && (
                <PaymentInvoicesModal payment={verFaturasDe} onClose={() => setVerFaturasDe(null)} />
            )}
            {verInfoDe && (
                <PaymentInfoModal payment={verInfoDe} onClose={() => setVerInfoDe(null)} />
            )}
            {excluindo && (
                <DeletePaymentDialog
                    payment={excluindo}
                    onClose={() => setExcluindo(null)}
                    onDeleted={recarregar}
                />
            )}
        </>
    );
};

export default PaymentsPage;
